import { useEffect, useRef, useState } from "react";

import { computePeaks, fetchAudioBuffer } from "../../lib/waveform";

/**
 * Dibuja la forma de onda de un fragmento de audio en un canvas que
 * llena el contenedor padre.
 *
 * El padre debe tener width/height calculados (lo usamos para la resolución
 * del canvas). Recalcula sólo cuando cambian src/in/out/width.
 */
export default function Waveform(props: {
  src: string;
  inMs: number;
  outMs: number;
  width: number;
  height: number;
  color?: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setError(false);
    fetchAudioBuffer(props.src).then((buf) => {
      if (!alive || !ref.current) return;
      const dpr = window.devicePixelRatio || 1;
      const canvas = ref.current;
      const w = Math.max(1, Math.floor(props.width));
      const h = Math.max(1, Math.floor(props.height));
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(dpr, dpr);

      const buckets = Math.max(8, w);
      const { mins, maxs } = computePeaks(buf, props.inMs / 1000, props.outMs / 1000, buckets);

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = props.color || "rgba(91,199,255,0.65)";
      const mid = h / 2;
      for (let i = 0; i < buckets; i++) {
        const x = (i / buckets) * w;
        const yMin = mid + mins[i] * mid;
        const yMax = mid + maxs[i] * mid;
        // dibujamos al menos 1 px de alto
        ctx.fillRect(x, Math.min(yMin, yMax), Math.max(1, w / buckets), Math.max(1, Math.abs(yMax - yMin)));
      }
    }).catch(() => alive && setError(true));
    return () => { alive = false; };
  }, [props.src, props.inMs, props.outMs, props.width, props.height, props.color]);

  if (error) return null;
  return (
    <canvas
      ref={ref}
      style={{
        position: "absolute", inset: 0, width: "100%", height: "100%",
        pointerEvents: "none",
      }}
    />
  );
}
